/**
 * ai-api — typed wrappers for the AI-assisted creation data layer, through the
 * @v0y4ge/api-client `apiRaw` choke point.
 *
 * The AI ops (`aiAssist`, `aiRefine`, `getAiProviderSettings`,
 * `putAiProviderSettings`, `deleteAiProviderSettings`,
 * `testAiProviderConnection`, `listAiProviderModels`) are LIVE named ops in
 * @v0y4ge/api-contracts + api-client, backed by the stateful mocks, and are
 * imported directly below — nothing in this module is seed-served. (Stale
 * seed-layer wording removed 2026-07-18 per the docket green-light note.)
 *
 * BYOK per tenant: the tenant stores its OWN provider key; all AI cost bills
 * the tenant. Reads never return the plaintext key — only a masked keyHint.
 */
import {
  ApiError,
  getAiProviderSettings as _getAiProviderSettings,
  putAiProviderSettings as _putAiProviderSettings,
  deleteAiProviderSettings as _deleteAiProviderSettings,
  testAiProviderConnection as _testAiProviderConnection,
  listAiProviderModels as _listAiProviderModels,
  aiAssist as _aiAssist,
  aiRefine as _aiRefine,
} from "@v0y4ge/api-client";
import { reportOpSource } from "../../shell/opSource";
import { t as i18nT } from "../../lib/i18n";

export { ApiError };

/** Data-source transparency (2026-07-16 convention): report each op result's
 *  `__source` marker so screens can flag non-integrated calls. Clean backend
 *  responses never carry the marker. */
const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });

export type AiProvider = "anthropic" | "openai" | "gemini";
export type AiProviderStatus = "configured" | "not_configured";

/** Masked read shape (GET /v1/admin/settings/ai). Never carries the API key. */
export interface AiProviderSettings {
  status: AiProviderStatus;
  provider?: AiProvider | null;
  keyHint?: string | null;   // last 4 chars, e.g. "…0000"
  model?: string | null;
  organizationId?: string | null; // OpenAI
  projectId?: string | null;       // Gemini
  baseUrl?: string | null;
  updatedAt?: string | null;
}
export interface PutAiProviderInput {
  provider: AiProvider;
  /** Required the FIRST time a provider is configured; omit to switch back to a
   *  provider that already has a stored key (activate without re-entry). */
  apiKey?: string;         // 20–500 chars
  model?: string;
  organizationId?: string;
  projectId?: string;
  baseUrl?: string;
}
/** getAiProviderSettings result: the ACTIVE provider's settings + every provider
 *  that has a stored key (retained across switches). */
export interface AiSettingsResult {
  settings: AiProviderSettings;
  configuredProviders: AiProvider[];
}
export interface TestConnectionResult {
  ok: boolean;
  latencyMs?: number;
  code?: string;     // e.g. "invalid_key" when ok:false
  message?: string;
}

export type AiSkill = "event-creation" | "service-creation" | "user-creation";
export interface AiMissingField {
  field: string; label: string; reason: string; requiredForPublish: boolean;
}
export interface AiUsage {
  provider: string; model: string; inputTokens: number; outputTokens: number; durationMs: number;
}
/** The event draft === CreationEventInput (brief §3) — ready for createEvent()
 *  unchanged. The wizard maps it onto its config in CreationStore.prefillFromAiDraft. */
export interface AiEventDraft {
  title?: string; description?: string; category?: string; type?: string;
  format?: "in_person" | "virtual" | "hybrid"; visibility?: string;
  tags?: string[]; imageUrl?: string | null;
  locationId?: string | null; locationAddress?: string | null; meetingLink?: string | null;
  startsAt?: string | null; endsAt?: string | null;
  repeatKind?: string; repeatConfig?: Record<string, unknown>;
  capacity?: number | null; allowWaitlist?: boolean; waitlistCapacity?: number | null; waitlistAutoPromote?: boolean;
  ticketTypeIds?: string[]; ticketTypeConfigs?: Record<string, unknown>;
  registrationQuestions?: unknown[];
  timezone?: string; // config-owned (tenant setting), returned for the wizard to display
  [k: string]: unknown;
}
export interface AiAssistResult {
  skill: AiSkill; draft: AiEventDraft; assumptions: string[]; missing: AiMissingField[]; usage: AiUsage;
  /** Optional conversational reply from the AI — lets the draft be refined
   *  iteratively (acknowledges changes, asks for content it still needs).
   *  Additive; absent on backends that don't return it (UI synthesizes a summary). */
  reply?: string;
}
export interface AiAssistInput {
  skill: AiSkill; prompt: string; context?: Record<string, unknown>;
}
/** "Iterate with AI": apply one natural-language change to the CURRENT draft.
 *  Returns the COMPLETE updated draft (replace wizard state, never merge) + a
 *  chat `reply`. Send the wizard's latest draft on every turn (stateless). */
export interface AiRefineInput {
  skill: AiSkill;
  instruction: string;
  draft: AiEventDraft;
  history?: { role: "user" | "assistant"; content: string }[];
  context?: Record<string, unknown>;
}

/* ── Real client ops (multi-provider overlay 03d81dfc) ──────────────────────
 * Wrap the named @v0y4ge/api-client callers. Three providers; exactly ONE is
 * ACTIVE per tenant (it serves aiAssist = the "default"). Keys are stored per
 * provider and retained on switch, so activating a configured provider needs no
 * key. tag() reports each result's __source marker (real responses omit it). */
export const getAiProviderSettings = () =>
  tag("getAiProviderSettings", _getAiProviderSettings() as unknown as Promise<AiSettingsResult>);

/** PUT activates `input.provider`. A PUT overwrites model/baseUrl with what it
 *  sends (omitted → cleared to defaults), so callers pass the form's current
 *  values every save. apiKey omitted → activate a provider that already has a key. */
export const putAiProviderSettings = (input: PutAiProviderInput) =>
  tag("putAiProviderSettings", _putAiProviderSettings({
    provider: input.provider,
    ...(input.apiKey ? { apiKey: input.apiKey } : {}),
    ...(input.model ? { model: input.model } : {}),
    ...(input.baseUrl ? { baseUrl: input.baseUrl } : {}),
  } as Parameters<typeof _putAiProviderSettings>[0]) as unknown as Promise<AiSettingsResult>);

/** With a provider: remove only that key. Without: remove ALL (full reset). */
export const deleteAiProviderSettings = (provider?: AiProvider) =>
  tag("deleteAiProviderSettings", (_deleteAiProviderSettings as (p?: AiProvider) => Promise<unknown>)(provider) as unknown as Promise<{ deleted: boolean }>);

/** Empty body validates the active stored key; `{provider}` targets a stored
 *  non-active provider; `{apiKey, baseUrl}` validates a typed key pre-save. */
export const testAiProviderConnection = (input?: { provider?: AiProvider; apiKey?: string; baseUrl?: string }) =>
  tag("testAiProviderConnection", (_testAiProviderConnection as (i?: unknown) => Promise<unknown>)(input) as unknown as Promise<TestConnectionResult>);

/* ── Assist (prompt → contract-validated draft) ─────────────────────────── */
export const aiAssist = (input: AiAssistInput) =>
  tag("aiAssist", _aiAssist(input as unknown as Parameters<typeof _aiAssist>[0]) as unknown as Promise<AiAssistResult>);

/** Refine ("Iterate with AI" chat): edits the draft you pass (does NOT
 *  regenerate from scratch). Returns the full updated draft + a chat reply. */
export const aiRefine = (input: AiRefineInput) =>
  tag("aiRefine", _aiRefine(input as unknown as Parameters<typeof _aiRefine>[0]) as unknown as Promise<AiAssistResult>);

/* ── Live model list (the provider is the source of truth, via the stored key) ──
 * The backend fetches the provider's models with the tenant's BYOK key and
 * returns { models: {id,displayName}[], source }. We map to the UI's string[]
 * (+ fetchedAt when the list is live). The curated AI_PROVIDERS[].models is the
 * offline fallback shown before a key is connected. So the key determines the list. */
/** Model list per provider — pass the dropdown's current selection so the list
 *  follows it before a key is saved (source:'builtin' until that provider has a
 *  key, then 'live'). */
export interface ProviderModelsResult { provider: AiProvider; models: string[]; fetchedAt?: string | null; }
export const listAiProviderModels = (provider?: AiProvider) =>
  tag("listAiProviderModels", (_listAiProviderModels as (p?: AiProvider) => Promise<unknown>)(provider).then((r) => {
    const rr = r as { models?: Array<string | { id?: string }>; source?: string };
    return {
      provider: provider || "anthropic",
      models: (rr.models || []).map((m) => (typeof m === "string" ? m : (m.id || ""))).filter(Boolean),
      fetchedAt: rr.source === "live" ? new Date().toISOString() : null,
    } as ProviderModelsResult;
  }));

/** Provider catalogue for Settings → AI. All three are live (multi-provider
 *  overlay); the active provider is the default that serves "Build with AI".
 *  Per-provider default models mirror the worker defaults (drop §4). ui-config. */
export interface AiProviderExtra { key: "organizationId" | "projectId" | "baseUrl"; label: string; labelKey: string; placeholder: string; placeholderKey: string; optional: boolean; }
export interface AiProviderDef {
  id: AiProvider; name: string; nameKey: string; available: boolean; blurb: string;
  keyLabel: string; keyPlaceholder: string; keyHelp: string;
  models: string[]; extras: AiProviderExtra[];
}
export const AI_PROVIDERS: AiProviderDef[] = [
  {
    id: "anthropic", nameKey: "ai.api.anthropic", get name() { return i18nT("ai.api.anthropic"); }, available: true,
    blurb: "Claude models.",
    keyLabel: "API key", keyPlaceholder: "sk-ant-…", keyHelp: "From console.anthropic.com → API Keys. Stored encrypted; billed to your Anthropic account.",
    models: ["claude-sonnet-4-6", "claude-opus-4-6", "claude-haiku-4-5"],
    extras: [{ key: "baseUrl", labelKey: "ai.api.customBaseUrl", get label() { return i18nT("ai.api.customBaseUrl"); }, placeholderKey: "ai.api.httpsApiAnthropicCom", get placeholder() { return i18nT("ai.api.httpsApiAnthropicCom"); }, optional: true }],
  },
  {
    id: "openai", nameKey: "ai.api.openai", get name() { return i18nT("ai.api.openai"); }, available: true,
    blurb: "GPT models.",
    keyLabel: "API key", keyPlaceholder: "sk-…", keyHelp: "From platform.openai.com → API Keys. Stored encrypted; billed to your OpenAI account.",
    models: ["gpt-5.6-terra", "gpt-5-mini", "gpt-4.1", "o4"],
    extras: [
      { key: "organizationId", labelKey: "ai.api.organizationId", get label() { return i18nT("ai.api.organizationId"); }, placeholderKey: "ai.api.org", get placeholder() { return i18nT("ai.api.org"); }, optional: true },
      { key: "baseUrl", labelKey: "ai.api.customBaseUrl", get label() { return i18nT("ai.api.customBaseUrl"); }, placeholderKey: "ai.api.httpsApiOpenaiCom", get placeholder() { return i18nT("ai.api.httpsApiOpenaiCom"); }, optional: true },
    ],
  },
  {
    id: "gemini", nameKey: "ai.api.googleGemini", get name() { return i18nT("ai.api.googleGemini"); }, available: true,
    blurb: "Gemini models.",
    keyLabel: "API key", keyPlaceholder: "AIza…", keyHelp: "From Google AI Studio or a Vertex service account. Stored encrypted; billed to your Google account.",
    models: ["gemini-3.5-flash", "gemini-2.5-pro", "gemini-2.5-flash"],
    extras: [
      { key: "projectId", labelKey: "ai.api.projectId", get label() { return i18nT("ai.api.projectId"); }, placeholderKey: "ai.api.myGcpProject", get placeholder() { return i18nT("ai.api.myGcpProject"); }, optional: true },
      { key: "baseUrl", labelKey: "ai.api.customBaseUrl", get label() { return i18nT("ai.api.customBaseUrl"); }, placeholderKey: "ai.api.httpsGenerativelanguageGoogleapisCom", get placeholder() { return i18nT("ai.api.httpsGenerativelanguageGoogleapisCom"); }, optional: true },
    ],
  },
];
// NOTE: the arrays above are only the OFFLINE FALLBACK. When a key is connected,
// SettingsAI replaces them with the live list from listAiProviderModels() so the
// options track whatever the provider currently offers — no code change to add a
// model. Keep this list short + current-generation; it's the "no key yet" view.
export const providerDef = (id?: string | null): AiProviderDef =>
  AI_PROVIDERS.find((p) => p.id === id) || AI_PROVIDERS[0];

/** Human-readable copy for the brief's §9 error codes — surfaced on assist failure. */
export function aiErrorCopy(err: unknown): { code: string; title: string; detail: string; configure?: boolean } {
  const e = err as { code?: string; message?: string };
  const code = e?.code || "UNKNOWN";
  switch (code) {
    case "ai_not_configured": return { code, title: i18nT("ai.api.noAiProviderConnected", "No AI provider connected"), detail: "Connect a provider key under Settings → AI to build with AI.", configure: true };
    case "ai_invalid_credentials": return { code, title: i18nT("ai.api.providerRejectedTheKey", "Provider rejected the key"), detail: "The stored API key was rejected. Update it under Settings → AI, then try again.", configure: true };
    case "ai_rate_limited": return { code, title: i18nT("ai.api.dailyAiLimitReached", "Daily AI limit reached"), detail: "This workspace hit its daily assist cap. Try again later or build from scratch." };
    case "ai_provider_error": return { code, title: i18nT("ai.api.theAiProviderIsUnavailable", "The AI provider is unavailable"), detail: "We couldn't reach the model provider. Try again in a moment." };
    case "ai_could_not_parse": return { code, title: i18nT("ai.api.couldnTStructureThatRequest", "Couldn't structure that request"), detail: "The model couldn't turn that prompt into an event. Try rephrasing with the date, capacity and format." };
    case "VALIDATION_ERROR": return { code, title: i18nT("ai.api.checkYourPrompt", "Check your prompt"), detail: e?.message || "The prompt was empty or too long (max 4000 characters)." };
    default: return { code, title: i18nT("ai.api.couldnTGenerateTheEvent", "Couldn't generate the event"), detail: e?.message || "Something went wrong. Try again, or build from scratch." };
  }
}
